import React, { useState, useEffect } from 'react';
import { FileText, DollarSign, Calendar, Plus, CheckCircle, Clock, AlertCircle, Download, Copy, Archive, Trash2, MoreHorizontal, Eye, Send } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../ui/dropdown-menu';
import api from '../../utils/api';
import { toast } from 'sonner';

const Invoicing = () => {
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    client_id: '',
    amount: '',
    description: '',
    service_package_id: '',
    due_date: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [invoicesRes, clientsRes, packagesRes] = await Promise.all([
        api.get('/invoices'),
        api.get('/clients'),
        api.get('/service-packages')
      ]);
      setInvoices(invoicesRes.data);
      setClients(clientsRes.data);
      setPackages(packagesRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/invoices', formData);
      toast.success('Invoice created successfully!');
      setDialogOpen(false);
      setFormData({
        client_id: '',
        amount: '',
        description: '',
        service_package_id: '',
        due_date: ''
      });
      fetchData();
    } catch (error) {
      console.error('Failed to create invoice:', error);
      toast.error('Failed to create invoice');
    }
  };

  const handleStatusUpdate = async (invoiceId, newStatus) => {
    try {
      await api.put(`/invoices/${invoiceId}/status?status=${newStatus}`);
      toast.success('Status updated!');
      fetchData();
    } catch (error) {
      console.error('Failed to update status:', error);
      toast.error('Failed to update status');
    }
  };

  const handleDelete = async (invoiceId) => {
    if (!window.confirm('Are you sure you want to delete this invoice?')) return;
    try {
      await api.delete(`/invoices/${invoiceId}`);
      toast.success('Invoice deleted');
      fetchData();
    } catch (error) {
      console.error('Failed to delete invoice:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete invoice');
    }
  };

  const handleDuplicate = async (invoice) => {
    try {
      const duplicateData = {
        client_id: invoice.client_id,
        amount: invoice.amount,
        description: invoice.description,
        service_package_id: invoice.service_package_id || '',
        due_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
      };
      await api.post('/invoices', duplicateData);
      toast.success('Invoice duplicated!');
      fetchData();
    } catch (error) {
      console.error('Failed to duplicate invoice:', error);
      toast.error('Failed to duplicate invoice');
    }
  };

  const handleArchive = async (invoiceId) => {
    try {
      await api.put(`/invoices/${invoiceId}/archive`);
      toast.success('Invoice archived');
      fetchData();
    } catch (error) {
      console.error('Failed to archive invoice:', error);
      toast.error('Failed to archive invoice');
    }
  };

  const handleDownload = async (invoice) => {
    try {
      // Generate a simple invoice PDF/text for download
      const client = clients.find(c => c.id === invoice.client_id);
      const content = `
INVOICE
=====================================
Invoice Number: ${invoice.invoice_number}
Date: ${new Date(invoice.created_at).toLocaleDateString()}
Due Date: ${new Date(invoice.due_date).toLocaleDateString()}

BILL TO:
${client?.company_name || 'Client'}
${client?.email || ''}

DESCRIPTION:
${invoice.description}

AMOUNT DUE: $${parseFloat(invoice.amount).toFixed(2)}

STATUS: ${invoice.status.toUpperCase()}
=====================================
Thank you for your business!
      `.trim();

      const blob = new Blob([content], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `invoice_${invoice.invoice_number}.txt`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Invoice downloaded!');
    } catch (error) {
      console.error('Failed to download invoice:', error);
      toast.error('Failed to download invoice');
    }
  };

  const handleViewInvoice = (invoice) => {
    setSelectedInvoice(invoice);
    setViewDialogOpen(true);
  };

  const getStatusIcon = (status) => {
    if (status === 'paid') return <CheckCircle className="w-4 h-4 text-green-600" />;
    if (status === 'overdue') return <AlertCircle className="w-4 h-4 text-red-600" />;
    if (status === 'sent') return <Clock className="w-4 h-4 text-blue-600" />;
    return <FileText className="w-4 h-4 text-gray-600" />;
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'bg-gray-100 text-gray-700',
      sent: 'bg-blue-100 text-blue-700',
      paid: 'bg-green-100 text-green-700',
      overdue: 'bg-red-100 text-red-700',
      archived: 'bg-purple-100 text-purple-700'
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  const getClientName = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    return client?.company_name || 'Unknown Client';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2" data-testid="invoicing-title">Invoicing</h2>
          <p className="text-gray-600">Manage client invoices and billing</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button
              data-testid="create-invoice-button"
              className="bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Invoice
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Create New Invoice</DialogTitle>
              <DialogDescription className="sr-only">
                Create a new invoice for a client
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="client_id">Client</Label>
                <Select value={formData.client_id} onValueChange={(value) => setFormData({ ...formData, client_id: value })} required>
                  <SelectTrigger data-testid="invoice-client-select">
                    <SelectValue placeholder="Select client" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map((client) => (
                      <SelectItem key={client.id} value={client.id}>
                        {client.company_name || `Client ${client.id.substring(0, 8)}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="service_package_id">Service Package (Optional)</Label>
                <Select value={formData.service_package_id} onValueChange={(value) => {
                  const pkg = packages.find(p => p.id === value);
                  setFormData({ 
                    ...formData, 
                    service_package_id: value,
                    amount: pkg ? pkg.price.toString() : formData.amount,
                    description: pkg ? pkg.name : formData.description
                  });
                }}>
                  <SelectTrigger data-testid="invoice-package-select">
                    <SelectValue placeholder="Select package" />
                  </SelectTrigger>
                  <SelectContent>
                    {packages.map((pkg) => (
                      <SelectItem key={pkg.id} value={pkg.id}>
                        {pkg.name} - ${pkg.price}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="amount">Amount ($)</Label>
                <Input
                  id="amount"
                  data-testid="invoice-amount-input"
                  type="number"
                  step="0.01"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  data-testid="invoice-description-input"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  required
                  rows={3}
                />
              </div>
              <div>
                <Label htmlFor="due_date">Due Date</Label>
                <Input
                  id="due_date"
                  data-testid="invoice-due-date-input"
                  type="date"
                  value={formData.due_date}
                  onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                  required
                />
              </div>
              <Button
                data-testid="submit-invoice-button"
                type="submit"
                className="w-full bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white"
              >
                Create Invoice
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Mobile Card View */}
      <div className="md:hidden space-y-4">
        {invoices.length === 0 ? (
          <div className="glass rounded-xl p-8 text-center">
            <FileText className="w-12 h-12 mx-auto text-gray-300 mb-3" />
            <p className="text-gray-500">No invoices yet. Create your first invoice!</p>
          </div>
        ) : (
          invoices.map((invoice, index) => (
            <div 
              key={invoice.id} 
              className="glass rounded-xl p-4 cursor-pointer hover:shadow-lg transition-shadow"
              onClick={() => handleViewInvoice(invoice)}
              data-testid={`invoice-card-${index}`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  {getStatusIcon(invoice.status)}
                  <span className="font-semibold text-gray-900">{invoice.invoice_number}</span>
                </div>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(invoice.status)}`}>
                  {invoice.status}
                </span>
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">Client</span>
                  <span className="text-sm font-medium text-gray-900">{getClientName(invoice.client_id)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">Amount</span>
                  <span className="text-lg font-bold text-teal-600">${parseFloat(invoice.amount).toFixed(2)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">Due Date</span>
                  <span className="text-sm text-gray-700">{new Date(invoice.due_date).toLocaleDateString()}</span>
                </div>
              </div>
              
              <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDownload(invoice);
                  }}
                  className="text-gray-600 hover:text-teal-600"
                >
                  <Download className="w-4 h-4 mr-1" />
                  Download
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                      <MoreHorizontal className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleViewInvoice(invoice); }}>
                      <Eye className="w-4 h-4 mr-2" />
                      View Details
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleDuplicate(invoice); }}>
                      <Copy className="w-4 h-4 mr-2" />
                      Duplicate
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    {invoice.status === 'draft' && (
                      <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleStatusUpdate(invoice.id, 'sent'); }}>
                        <Send className="w-4 h-4 mr-2" />
                        Mark as Sent
                      </DropdownMenuItem>
                    )}
                    {invoice.status !== 'paid' && invoice.status !== 'archived' && (
                      <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleStatusUpdate(invoice.id, 'paid'); }}>
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Mark as Paid
                      </DropdownMenuItem>
                    )}
                    {invoice.status === 'paid' && (
                      <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleArchive(invoice.id); }}>
                        <Archive className="w-4 h-4 mr-2" />
                        Archive
                      </DropdownMenuItem>
                    )}
                    {invoice.status === 'draft' && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem 
                          onClick={(e) => { e.stopPropagation(); handleDelete(invoice.id); }}
                          className="text-red-600"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Desktop Table View */}
      <div className="hidden md:block glass rounded-xl overflow-hidden">
        <table className="w-full" data-testid="invoices-table">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Invoice #</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Due Date</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {invoices.length === 0 ? (
              <tr>
                <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                  No invoices yet. Create your first invoice!
                </td>
              </tr>
            ) : (
              invoices.map((invoice, index) => (
                <tr 
                  key={invoice.id} 
                  className="hover:bg-gray-50 cursor-pointer" 
                  onClick={() => handleViewInvoice(invoice)}
                  data-testid={`invoice-row-${index}`}
                >
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      {getStatusIcon(invoice.status)}
                      <span className="text-sm font-medium text-gray-900">{invoice.invoice_number}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {getClientName(invoice.client_id)}
                  </td>
                  <td className="px-6 py-4 text-sm font-semibold text-gray-900">${parseFloat(invoice.amount).toFixed(2)}</td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {new Date(invoice.due_date).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(invoice.status)}`}>
                      {invoice.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDownload(invoice);
                        }}
                        className="h-8 w-8 p-0"
                        title="Download Invoice"
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleViewInvoice(invoice)}>
                            <Eye className="w-4 h-4 mr-2" />
                            View Details
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleDuplicate(invoice)}>
                            <Copy className="w-4 h-4 mr-2" />
                            Duplicate
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          {invoice.status === 'draft' && (
                            <DropdownMenuItem onClick={() => handleStatusUpdate(invoice.id, 'sent')}>
                              <Send className="w-4 h-4 mr-2" />
                              Mark as Sent
                            </DropdownMenuItem>
                          )}
                          {invoice.status !== 'paid' && invoice.status !== 'archived' && (
                            <DropdownMenuItem onClick={() => handleStatusUpdate(invoice.id, 'paid')}>
                              <CheckCircle className="w-4 h-4 mr-2" />
                              Mark as Paid
                            </DropdownMenuItem>
                          )}
                          {invoice.status === 'paid' && (
                            <DropdownMenuItem onClick={() => handleArchive(invoice.id)}>
                              <Archive className="w-4 h-4 mr-2" />
                              Archive
                            </DropdownMenuItem>
                          )}
                          {invoice.status === 'draft' && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem 
                                onClick={() => handleDelete(invoice.id)}
                                className="text-red-600"
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Invoice View Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-teal-600" />
              Invoice Details
            </DialogTitle>
            <DialogDescription className="sr-only">
              View and manage invoice details including amount, due date, and actions
            </DialogDescription>
          </DialogHeader>
          {selectedInvoice && (
            <div className="space-y-6">
              {/* Invoice Header */}
              <div className="flex items-center justify-between pb-4 border-b border-gray-200">
                <div>
                  <p className="text-2xl font-bold text-gray-900">{selectedInvoice.invoice_number}</p>
                  <p className="text-sm text-gray-500">Created: {new Date(selectedInvoice.created_at).toLocaleDateString()}</p>
                </div>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(selectedInvoice.status)}`}>
                  {selectedInvoice.status}
                </span>
              </div>

              {/* Bill To */}
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase mb-1">Bill To</p>
                <p className="font-semibold text-gray-900">{getClientName(selectedInvoice.client_id)}</p>
              </div>

              {/* Description */}
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase mb-1">Description</p>
                <p className="text-gray-700">{selectedInvoice.description}</p>
              </div>

              {/* Amount & Due Date */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-xs font-medium text-gray-500 uppercase mb-1">Amount Due</p>
                  <p className="text-2xl font-bold text-teal-600">${parseFloat(selectedInvoice.amount).toFixed(2)}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-xs font-medium text-gray-500 uppercase mb-1">Due Date</p>
                  <p className="text-lg font-semibold text-gray-900">{new Date(selectedInvoice.due_date).toLocaleDateString()}</p>
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-wrap gap-2 pt-4 border-t border-gray-200">
                <Button
                  onClick={() => handleDownload(selectedInvoice)}
                  className="flex-1 bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download
                </Button>
                <Button
                  variant="outline"
                  onClick={() => handleDuplicate(selectedInvoice)}
                  className="flex-1"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Duplicate
                </Button>
                {selectedInvoice.status !== 'paid' && selectedInvoice.status !== 'archived' && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      handleStatusUpdate(selectedInvoice.id, 'paid');
                      setViewDialogOpen(false);
                    }}
                    className="flex-1 text-green-600 border-green-200 hover:bg-green-50"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Mark Paid
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Invoicing;
